import { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Tag, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#7C3AED", "#EC4899", "#3B82F6", "#10B981", "#F59E0B", "#EF4444"];

export default function CRMSegments() {
  const [segments, setSegments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = async () => {
    try {
      const [s, c] = await Promise.all([api.crmListSegments(), api.crmListContacts()]);
      setSegments(s); setContacts(c);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!confirm("Supprimer ce segment ?")) return;
    await api.crmDeleteSegment(id); refresh();
  };

  return (
    <div className="space-y-6 animate-fadeUp" data-testid="crm-segments-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--brand-pink)" }}>CRM Social</div>
          <h1 className="font-display text-4xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>Segments</h1>
          <p className="text-sm mt-1.5" style={{ color: "var(--text-muted)" }}>Regroupe tes contacts par niche, taille, engagement.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-segment-btn"
          className="cdo-gradient text-white text-sm font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 glow-purple">
          <Plus className="w-4 h-4" /> Nouveau segment
        </button>
      </header>

      {segments.length === 0 ? (
        <div className="surface p-12 text-center">
          <Tag className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} strokeWidth={1.2} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>Aucun segment — crée ton premier.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {segments.map((s, i) => {
            const count = contacts.filter(c => (c.segment_ids || []).includes(s.id)).length;
            return (
              <div key={s.id} data-testid={`segment-card-${i}`} className="surface surface-hover p-5 group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.color + "20" }}>
                      <Tag className="w-4 h-4" style={{ color: s.color }} />
                    </span>
                    <div>
                      <div className="font-semibold" style={{ color: "var(--text-primary)" }}>{s.name}</div>
                      <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{count} contact{count > 1 ? "s" : ""}</div>
                    </div>
                  </div>
                  <button onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {s.description && <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{s.description}</div>}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddSegment onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); refresh(); }} />}
    </div>
  );
}

function AddSegment({ onClose, onSuccess }) {
  const [f, setF] = useState({ name: "", description: "", color: "#7C3AED" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.crmCreateSegment(f); toast.success("Segment créé"); onSuccess(); }
    catch { toast.error("Erreur"); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="rounded-2xl p-6 w-full max-w-md space-y-4" style={{ background: "var(--bg-surface)" }} data-testid="add-segment-modal">
        <div className="flex items-center justify-between">
          <div className="font-display text-xl font-bold" style={{ color: "var(--text-primary)" }}>Nouveau segment</div>
          <button type="button" onClick={onClose}><X className="w-5 h-5" style={{ color: "var(--text-muted)" }} /></button>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nom</label>
          <input data-testid="sg-name" required value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: "var(--bg-base)", color: "var(--text-primary)", border: "1px solid var(--border-default)" }} />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Description</label>
          <textarea rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: "var(--bg-base)", color: "var(--text-primary)", border: "1px solid var(--border-default)" }} />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider block mb-2" style={{ color: "var(--text-muted)" }}>Couleur</label>
          <div className="flex gap-2">
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => setF({ ...f, color: c })}
                className={`w-8 h-8 rounded-lg transition ${f.color === c ? "ring-2 ring-offset-2" : ""}`}
                style={{ background: c, "--tw-ring-color": c }} />
            ))}
          </div>
        </div>
        <button disabled={loading} type="submit" data-testid="sg-submit"
          className="w-full cdo-gradient text-white font-semibold py-3 rounded-xl glow-purple disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "..." : "Créer"}
        </button>
      </form>
    </div>
  );
}
